import { getIdToken } from "./amplify";

const BASE = import.meta.env.VITE_API_BASE.replace(/\/$/, "");

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error("not authenticated");
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const data = await res.json();
      detail = data?.error ?? JSON.stringify(data);
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export interface Profile {
  sub: string;
  email: string;
  first_name: string;
  native_language: string;
  native_language_code: string;
  active_language: string;
  languages: string[];
  cefr_level: string;
  tz_offset_hours: number;
  quiet_start_hour: number;
  quiet_end_hour: number;
  daily_message_target: number;
  phone_unverified: string;
  onboarding_complete: boolean;
  activated: boolean;
  activated_at: number;
  is_admin: boolean;
  persona: string;
  voice_enabled?: boolean;
  email_weekly?: boolean;
  custom_persona?: Partial<{
    warmth: number;
    terseness: number;
    curiosity: number;
    formality: number;
    humor: number;
  }>;
}

export interface Progress {
  summary: {
    total_words: number;
    seen: number;
    active: number;
    learning: number;
    due_now: number;
    new_available: number;
    days_since_start: number;
  };
  lifetime: { tokens_in: number; tokens_out: number; cost_usd: number };
  last_30_days: UsageRow[];
  activated: boolean;
}

export interface DailyStatsRow {
  date: string;
  lang: string;
  seen: number;
  learning: number;
  active: number;
  mastery_dist: Record<string, number>;
  turns_count: number;
  engagement_rate: number;
  proactive_turns?: number;
  // native_ratio = % of native language in TUTOR outbound messages
  // user_native_ratio = % of native language in USER inbound messages
  // Plotted as two lines on the Journey page so the adoption flywheel
  // (user writes more target → tutor nudges up its target ratio) is
  // visible at a glance.
  native_ratio: number;
  user_native_ratio?: number;
  cefr_level: string;
  // CEFR-level-specific foundation metrics (the two the level gate reads):
  // how much of the current level's curriculum the user has mastered, and
  // how clean their grammar has been in recent turns.
  cefr_vocab_active?: number;
  cefr_vocab_total?: number;
  cefr_grammar_mastery?: number;
  backfilled?: boolean;
}

export interface CefrEvent {
  lang: string;
  epoch: number;
  from_level: string;
  to_level: string;
  reason: string;
}

export interface LevelBreakdown {
  level: string;
  vocab_active: number;
  vocab_total: number;
  vocab_pct: number;
  grammar_mastery: number;
}

export interface OrphanWord {
  word: string;
  mastery: number;
  reps: number;
}

export interface RecentMastered {
  word: string;
  gloss: string;
  mastery: number;
  reps: number;
  last_seen: number;
}

export interface GrammarErrorRank {
  tag: string;
  count: number;
  pct: number;
  // "hard" tags count toward grammar mastery (article_gender,
  // verb_conjugation, word_order, ...). "soft" tags are tracked but
  // don't penalize mastery (capitalization, code-switching, spelling).
  // Optional for back-compat with older API responses.
  kind?: "hard" | "soft";
}

export interface LiveSnapshot {
  seen: number;
  active: number;
  learning: number;
  due_now: number;
  cefr_level: string;
  cefr_vocab_active: number;
  cefr_vocab_total: number;
  grammar_mastery: number;
  pos_distribution: Record<string, number>;
  orphan_count: number;
  orphan_sample: OrphanWord[];
  recent_mastered: RecentMastered[];
  top_grammar_errors: GrammarErrorRank[];
}

export interface AgentEvent {
  // type ∈ {"tool_call", "followup_scheduled", "followup_delivered",
  //         "drill_scheduled", "drill_delivered", "weekly_review",
  //         "impasse_nudge"}
  type: string;
  epoch: number;
  // Event-specific payload — see docs/agents.md for shapes per type.
  detail: Record<string, unknown>;
}

export interface ProgressHistory {
  target_uid: string;
  lang: string;
  days: number;
  daily: DailyStatsRow[];
  cefr_events: CefrEvent[];
  current_cefr: string;
  level_breakdowns?: LevelBreakdown[];
  current_snapshot?: LiveSnapshot;
  agent_events?: AgentEvent[];
}

export interface UsageRow {
  date: string;
  tokens_in: number;
  tokens_out: number;
  haiku_in: number;
  haiku_out: number;
  sonnet_in: number;
  sonnet_out: number;
  msgs_in: number;
  msgs_out: number;
  cost_usd: number;
}

export interface VocabWord {
  word: string;
  gloss: string;
  pos: string;
  gender: string;
  level: string;
  reps: number;
  mastery: number;
}

export interface LinkCode {
  code: string;
  deep_link: string;
  expires_at: number;
}

export interface PosMix {
  lang: string;
  cefr_level: string;
  realized: Record<string, number>;
  target: Record<string, number>;
  max_skew_pp: number;
  warning: boolean;
  days_with_data: number;
}

export interface AdminUser {
  sub: string;
  email: string;
  first_name: string;
  active_language: string;
  cefr_level: string;
  tz_offset_hours: number;
  quiet_start_hour: number;
  quiet_end_hour: number;
  daily_message_target: number;
  activated_at: number;
  telegram_user_id: string;
  lifetime_tokens_in: number;
  lifetime_tokens_out: number;
  lifetime_cost_usd: number;
  lifetime_msgs_out: number;
  msg_limit: number;
  // Daily quota counters — these drive the cap, NOT lifetime_msgs_out.
  // `period_turns_used` resets at every UTC midnight; `daily_reset_at`
  // is the epoch of the next reset. Optional for backwards compat with
  // older API responses.
  period_turns_used?: number;
  daily_reset_at?: number;
  last_user_msg_at: number;
  last_bot_msg_at: number;
  is_admin: boolean;
  paused: boolean;
}

export interface PricingRate {
  model_ids: string[];
  input_usd_per_1m: number;
  output_usd_per_1m: number;
}

export interface PricingInfo {
  rates: PricingRate[];
  active_models: { tutor: string; signal_extractor: string; planner: string };
  msg_limit: number;
}

export interface TierCatalog {
  name: string;
  price_usd: number;
  turns: number;
  voice: number;
  languages: number;
  has_price: boolean;
}

export interface BillingStatus {
  tier: string;
  tier_name: string;
  subscription_status: string;
  turns_limit: number;
  turns_used: number;
  voice_limit: number;
  voice_used: number;
  languages_limit: number;
  period_reset_at: number;
  cancel_at_period_end: boolean;
  has_stripe_customer: boolean;
  billing_configured: boolean;
  tiers: Record<string, TierCatalog>;
}

export interface LanguageOption {
  code: string;
  name: string;
  endonym: string;
  /** True iff nouns take a definite article (de/es/fr/it/pt). */
  has_gendered_nouns?: boolean;
  /**
   * Map of gender → article (e.g. {m:"der", f:"die", n:"das"} for de).
   * Empty for languages without articles. Surface for the Today /
   * Vocabulary screens to render rows correctly without hard-coding.
   */
  gender_articles?: Record<string, string>;
}

export interface LanguagesResponse {
  languages: LanguageOption[];
  native_options: LanguageOption[];
  default: string;
  default_native: string;
}

export const api = {
  getMe: () => call<Profile>("GET", "/api/me"),
  getLanguages: () => call<LanguagesResponse>("GET", "/api/languages"),
  addLanguage: (code: string, makeActive = false) =>
    call<Profile>("POST", "/api/languages", { code, make_active: makeActive }),
  removeLanguage: (code: string) =>
    call<Profile>("DELETE", `/api/languages/${encodeURIComponent(code)}`),
  updateMe: (patch: Partial<Profile>) => call<Profile>("PATCH", "/api/me", patch),
  deleteMe: (confirm: string) => call<{ deleted_rows: number }>("DELETE", "/api/me", { confirm }),
  createLinkCode: () => call<LinkCode>("POST", "/api/link-code", {}),
  getProgress: (lang?: string) =>
    call<Progress>(
      "GET",
      lang ? `/api/progress?lang=${encodeURIComponent(lang)}` : "/api/progress",
    ),
  getProgressHistory: (opts: { lang?: string; days?: number; uid?: string } = {}) => {
    const params = new URLSearchParams();
    if (opts.lang) params.set("lang", opts.lang);
    if (opts.days) params.set("days", String(opts.days));
    if (opts.uid) params.set("uid", opts.uid);
    const q = params.toString();
    return call<ProgressHistory>(
      "GET",
      `/api/progress/history${q ? "?" + q : ""}`,
    );
  },
  getWords: (
    filter: "seen" | "learning" | "comfortable" | "due" | "new" | "all" = "seen",
    lang?: string,
  ) => {
    const params = new URLSearchParams({ filter });
    if (lang) params.set("lang", lang);
    return call<{ words: VocabWord[]; total: number }>(
      "GET",
      `/api/words?${params.toString()}`,
    );
  },
  listUsers: (cursor?: string) =>
    call<{ users: AdminUser[]; next_cursor: string | null }>(
      "GET",
      `/api/admin/users${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  getUserDetail: (sub: string) =>
    call<{
      profile: AdminUser;
      usage_last_30d: UsageRow[];
      recent_messages: unknown[];
      pos_mix: PosMix;
    }>(
      "GET",
      `/api/admin/user/${encodeURIComponent(sub)}`,
    ),
  adminUpdateUser: (sub: string, patch: Partial<AdminUser & { paused: boolean }>) =>
    call<AdminUser>("PATCH", `/api/admin/user/${encodeURIComponent(sub)}`, patch),
  postReviewOutcome: (word: string, rating: 1 | 2 | 3 | 4, lang?: string) =>
    call<{ word: string; mastery: number; reps: number; next_due: number }>(
      "POST",
      "/api/review_outcome",
      { word, rating, lang },
    ),
  getPricing: () => call<PricingInfo>("GET", "/api/admin/pricing"),
  getBillingStatus: () => call<BillingStatus>("GET", "/api/billing/status"),
  createCheckout: (tier: string, opts?: { next_path?: string }) =>
    call<{ url: string; id: string }>("POST", "/api/billing/checkout", { tier, ...(opts ?? {}) }),
  createPortal: () => call<{ url: string }>("POST", "/api/billing/portal", {}),

  listCandidates: (opts: { lang: string; kind?: "vocab" | "grammar"; status?: string }) => {
    const params = new URLSearchParams({ lang: opts.lang });
    if (opts.kind) params.set("kind", opts.kind);
    if (opts.status) params.set("status", opts.status);
    return call<{ candidates: CurriculumCandidate[]; lang: string; kind: string; status: string }>(
      "GET", `/api/admin/candidates?${params.toString()}`,
    );
  },
  approveCandidate: (body: {
    lang: string; kind: "vocab" | "grammar"; key: string;
    level?: string; pos?: string; gender?: string; gloss?: string;
    name?: string; summary?: string; tier?: number; id?: string;
  }) =>
    call<{ ok: boolean; promoted: Record<string, unknown> }>("POST", "/api/admin/candidates/approve", body),
  rejectCandidate: (body: { lang: string; kind: "vocab" | "grammar"; key: string }) =>
    call<{ ok: boolean }>("POST", "/api/admin/candidates/reject", body),
  getSystemHealth: () => call<SystemHealth>("GET", "/api/admin/system-health"),
};

export type HealthStatus = "green" | "amber" | "red";

export interface LambdaHealthRow {
  name: string;
  invocations: number;
  errors: number;
  error_rate: number;
  duration_p95_ms: number | null;
  last_invocation_at: number;
  status: HealthStatus;
}

export interface CronHealthRow {
  name: string;
  interval_seconds: number;
  last_run_at: number;
  age_seconds: number | null;
  status: HealthStatus;
}

export interface SystemHealth {
  generated_at: number;
  window_seconds: number;
  cached: boolean;
  lambdas: LambdaHealthRow[];
  tutor_failures: {
    last_hour_total: number;
    last_24h_total: number;
    sparkline_1h: number[];
    sparkline_24h: number[];
    status: HealthStatus;
  };
  memory_pipeline: {
    ops_applied_last_hour: number;
    extractor_invocations_last_hour: number;
    avg_ops_per_invocation: number;
    status: HealthStatus;
  };
  crons: CronHealthRow[];
  openrouter: {
    fallback_firings_last_hour: number;
    fallback_sparkline: number[];
    default_success_rate: number;
    approx_total_attempts: number;
    status: HealthStatus;
  };
  api_gateway: {
    rate_4xx: number;
    rate_5xx: number;
    p95_latency_ms: number | null;
    total_requests_last_hour: number;
    status: HealthStatus;
    configured: boolean;
  };
}

export interface CurriculumCandidate {
  // Common fields
  lang: string;
  status: string;        // "pending" | "judged" | "approved" | "rejected"
  total_count: number;
  distinct_users: number;
  first_seen?: number;
  last_seen?: number;
  example_sentence?: string;
  example_source?: string;
  level_a1?: number;
  level_a2?: number;
  level_b1?: number;
  level_b2?: number;
  level_c1?: number;
  level_c2?: number;
  level_unknown?: number;
  // Vocab-specific
  lemma?: string;
  error_flagged_count?: number;
  judged_valid?: boolean;
  pos?: string;
  judged_pos?: string;  // legacy, before the 6-bucket POS schema
  judged_gender?: string;
  judged_level?: string;
  judged_gloss?: string;
  // Grammar-specific
  tag?: string;
  examples?: string[];
  judged_covered?: boolean;
  judged_proposed_id?: string;
  judged_proposed_level?: string;
  judged_proposed_tier?: number;
  judged_proposed_name?: string;
  judged_proposed_summary?: string;
  // Shared judgment fields
  judged_confidence?: number;
  judged_notes?: string;
}
